import PropTypes from 'prop-types';

import { cn } from '#/lib/utils';
import PurchaseOrderStatusBadge from '#/modules/srmMgmt/purchaseOrder/components/PurchaseOrderStatusBadge';
import {
  displayOrDash,
  mapPurchaseOrderMeta,
} from '#/modules/srmMgmt/purchaseOrder/helper/mapPurchaseOrderMeta';

function PurchaseOrderHero({ meta, actions }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="flex min-w-0 flex-1 flex-col gap-5">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold text-gray-900">{meta.code}</h1>
            {meta.status ? <PurchaseOrderStatusBadge status={meta.status} /> : null}
          </div>
          <p className="mt-1 text-sm text-gray-600">
            供應商：
            {meta.supplierSubtitle}
          </p>
        </div>
      </div>
      {actions ? (
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          {actions}
        </div>
      ) : null}
    </div>
  );
}

PurchaseOrderHero.propTypes = {
  meta: PropTypes.shape({
    code: PropTypes.string,
    status: PropTypes.string,
    supplierSubtitle: PropTypes.string,
  }).isRequired,
  actions: PropTypes.node,
};

function PurchaseOrderInfoGrid({ meta }) {
  return (
    <div
      className={cn(
        'mt-4 grid grid-cols-1 gap-x-4 gap-y-3 border-t border-slate-200 pt-4',
        'sm:grid-cols-2 md:grid-cols-4',
      )}
    >
      <div>
        <p className="text-xs text-gray-500">建立</p>
        <p className="mt-0.5 text-sm font-medium wrap-anywhere text-gray-900">{meta.createDate}</p>
      </div>
      <div>
        <p className="text-xs text-gray-500">修改</p>
        <p className="mt-0.5 text-sm font-medium wrap-anywhere text-gray-900">{meta.reviseDate}</p>
      </div>
      <div>
        <p className="text-xs text-gray-500">{meta.closeLabel}</p>
        <p
          className="mt-0.5 text-sm font-medium wrap-anywhere text-gray-900"
          title={meta.terminateReason ? `中止原因：${meta.terminateReason}` : undefined}
        >
          {meta.closeDate}
        </p>
      </div>
      <div className="hidden md:block" aria-hidden="true" />

      <div>
        <p className="text-xs text-gray-500">採購日</p>
        <p className="mt-0.5 text-sm font-medium wrap-anywhere text-gray-900">{meta.purchaseOrderDate}</p>
      </div>
      <div>
        <p className="text-xs text-gray-500">付款條件</p>
        <p className="mt-0.5 text-sm font-medium wrap-anywhere text-gray-900">{meta.paymentTerm}</p>
      </div>
      <div>
        <p className="text-xs text-gray-500">預設倉</p>
        <p className="mt-0.5 text-sm font-medium wrap-anywhere text-gray-900">{meta.defaultWarehouse}</p>
      </div>
      <div>
        <p className="text-xs text-gray-500">幣別／匯率</p>
        <p className="mt-0.5 text-sm font-medium wrap-anywhere text-gray-900">
          {meta.currency}
          {' · '}
          {meta.exchangeRate}
        </p>
      </div>

      {meta.hasAmount ? (
        <>
          <div>
            <p className="text-xs text-gray-500">未稅小計</p>
            <p className="mt-0.5 text-sm font-medium wrap-anywhere text-gray-900">{meta.subtotalAmount}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">稅額</p>
            <p className="mt-0.5 text-sm font-medium wrap-anywhere text-gray-900">{meta.taxAmount}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">合計</p>
            <p className="mt-0.5 text-sm font-medium wrap-anywhere text-gray-900">{meta.totalAmount}</p>
          </div>
          <div className="hidden md:block" aria-hidden="true" />
        </>
      ) : null}

      <div className="sm:col-span-2 md:col-span-2">
        <p className="text-xs text-gray-500">採購事由</p>
        <p className="mt-0.5 text-sm font-medium wrap-break-word text-gray-900">
          {displayOrDash(meta.issueReason)}
        </p>
      </div>
      <div className="sm:col-span-2 md:col-span-2">
        <p className="text-xs text-gray-500">備註</p>
        <p className="mt-0.5 text-sm font-medium wrap-break-word text-gray-900">
          {displayOrDash(meta.remark)}
        </p>
      </div>
    </div>
  );
}

PurchaseOrderInfoGrid.propTypes = {
  meta: PropTypes.object.isRequired,
};

const PurchaseOrderMainInfoCard = ({
  detail,
  embedded = false,
  actions = null,
}) => {
  const meta = mapPurchaseOrderMeta(detail);

  const content = (
    <>
      <PurchaseOrderHero meta={meta} actions={actions} />
      <PurchaseOrderInfoGrid meta={meta} />
    </>
  );

  if (embedded) {
    return (
      <div className="border-b border-slate-200 p-5">
        {content}
      </div>
    );
  }

  return (
    <div className="receipt-card-elevate">
      <div className="receipt-card-surface p-5">
        {content}
      </div>
    </div>
  );
};

PurchaseOrderMainInfoCard.propTypes = {
  detail: PropTypes.object.isRequired,
  embedded: PropTypes.bool,
  actions: PropTypes.node,
};

export default PurchaseOrderMainInfoCard;
